import React, { useEffect, useState } from "react";
import {
  Plus,
  Search,
  ListRestart,
  Download,
  X,
  FileLock
} from "lucide-react";
import LicenseForm from "../components/forms/LicenseGenerationForm";
import Pagination from "../components/common/Pagination";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable";
import EmptyState from "../components/common/EmptyState";
import useAlert from "../components/hook/useAlert";
import callApi from "../helpers/networkHelper";
import API_CONFIG from "../config/apiConstant";

/* CONSTANTS */
const PAGE_SIZE = 10;

/* HELPERS */
const buildQueryString = (params) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") q.append(k, v);
  });
  return q.toString();
};

const getReadableErrorMessage = (error) => {
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return "Something went wrong. Please try again.";
};

const getDerivedStatus = (license) => {
  if (license.expiresAt && new Date() > new Date(license.expiresAt)) {
    return "EXPIRED";
  }
  return "ACTIVE";
};

// Uses the status tokens rather than fixed green-400/red-400, which were tuned
// for a dark background and washed out on the light theme.
const StatusBadge = ({ status }) => {
  const base =
    "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border";

  const isActive = status === "ACTIVE";

  return (
    <span
      className={base}
      style={{
        color: isActive ? "var(--success)" : "var(--danger)",
        borderColor: isActive ? "var(--success-border)" : "var(--danger-border)",
        background: isActive ? "var(--success-bg)" : "var(--danger-bg)",
      }}
    >
      {isActive ? "ACTIVE" : "EXPIRED"}
    </span>
  );
};

/* MAIN COMPONENT */
const License = () => {
  const { showSuccess, showError } = useAlert();

  const [licenses, setLicenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [rans, setRans] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  /* FETCH DROPDOWNS */
  const fetchMasters = async () => {
    try {
      const [c, p, r] = await Promise.all([
        callApi("GET", API_CONFIG.CLIENT.GET.ENDPOINT + "?isActive=true"),
        callApi("GET", API_CONFIG.PARTNER.GET.ENDPOINT + "?isActive=true"),
        callApi("GET", API_CONFIG.RAN.GET.ENDPOINT + "?isActive=true"),
      ]);

      setClients(c.data.data || []);
      setPartners(p.data.data || []);
      setRans(r.data.data || []);
    } catch (e) {
      showError("Failed to load master data");
    }
  };

  /* FETCH LICENSES */
  const fetchLicenses = async (initial = false) => {
    initial ? setIsPageLoading(true) : setIsTableLoading(true);

    try {

      const qs = buildQueryString({
        page,
        limit: PAGE_SIZE,
        search
      });
      const res = await callApi(
        API_CONFIG.LICENSE.LIST.METHOD,
        `${API_CONFIG.LICENSE.LIST.ENDPOINT}?${qs}`
      );

      setLicenses(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      showError(getReadableErrorMessage(e));
    } finally {
      setIsPageLoading(false);
      setIsTableLoading(false);
    }
  };

  const handleDownload = async (licenseId) => {
    try {
      console.log(`Starting download for license: ${licenseId}`);
      
      const res = await callApi(
        API_CONFIG.LICENSE.DOWNLOAD.METHOD,
        API_CONFIG.LICENSE.DOWNLOAD.ENDPOINT.replace(":licenseId", licenseId),
        null,
        { "Content-Type": "application/json" }, // headers
        null, // cancelToken
        "blob" // responseType - this should be the 6th parameter
      );

      console.log(`Download response received, size: ${res.data.size} bytes`);

      // Verify that we received a blob
      if (!res.data || res.data.size === 0) {
        throw new Error("Empty response received from server");
      }

      // Create blob URL from the ZIP response
      const blob = new Blob([res.data], { type: 'application/zip' });
      
      // Verify blob creation
      if (blob.size === 0) {
        throw new Error("Failed to create blob from response data");
      }
      
      const url = window.URL.createObjectURL(blob);

      // Create temp anchor for download
      const a = document.createElement("a");
      a.href = url;
      a.download = `${licenseId}.zip`; // Download as ZIP file
      document.body.appendChild(a);
      a.click();

      // Cleanup
      a.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(`License ZIP file downloaded successfully (${blob.size} bytes)`);
    } catch (e) {
      console.error("Download error:", e);
      
      // Check if it's a network error or server error
      if (e?.response?.status) {
        showError(
          `Download failed (${e.response.status}): ${e?.response?.data?.message || "Server error"}`
        );
      } else if (e?.message?.includes("blob") || e?.message?.includes("Empty")) {
        showError("Download failed: Invalid or empty response from server");
      } else {
        showError(
          e?.response?.data?.message ||
          e?.message ||
          "Failed to download license"
        );
      }
    }
  };

  useEffect(() => {
    fetchMasters();
    fetchLicenses(true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isPageLoading) fetchLicenses(false);
    // eslint-disable-next-line
  }, [page, search, statusFilter]);

  /* CREATE LICENSE */
  const handleCreate = async (payload) => {
    setIsSubmitting(true);
    try {
      await callApi(
        API_CONFIG.LICENSE.CREATE.METHOD,
        API_CONFIG.LICENSE.CREATE.ENDPOINT,
        payload
      );
      showSuccess("License generated successfully");
      setIsModalOpen(false);
      fetchLicenses();
    } catch (e) {
      showError(getReadableErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredLicenses = licenses.filter(l => {
    if (statusFilter === "ACTIVE") {
      return getDerivedStatus(l) === "ACTIVE";
    }

    if (statusFilter === "EXPIRED") {
      return getDerivedStatus(l) === "EXPIRED";
    }

    return true;
  });

  const hasActiveFilters = search.trim() !== "" || statusFilter !== "all";

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setPage(1);
  };

  const columns = [
    {
      key: "licenseId",
      label: "License ID",
      nowrap: true,
      render: (l) => (
        <span className="font-medium" style={{ color: "var(--brand)" }}>
          {l.licenseId}
        </span>
      ),
    },
    { key: "licenseType", label: "License Type", nowrap: true },
    {
      key: "status",
      label: "Status",
      nowrap: true,
      render: (l) => <StatusBadge status={getDerivedStatus(l)} />,
    },
    {
      key: "expiresAt",
      label: "Expiry",
      nowrap: true,
      render: (l) => (
        <span style={{ color: "var(--main-text-muted)" }}>
          {l.expiresAt ? (
            <span title={`Expires on ${new Date(l.expiresAt).toLocaleString()}`}>
              {new Date(l.expiresAt).toLocaleDateString("en-GB")}
            </span>
          ) : (
            "Never"
          )}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      nowrap: true,
      align: "right",
      render: (l) =>
        getDerivedStatus(l) === "ACTIVE" ? (
          <button
            onClick={() => handleDownload(l.licenseId)}
            className="inline-flex items-center gap-1 hover:opacity-80"
            style={{ color: "var(--brand)" }}
            title="Download license file in ZIP format"
          >
            <Download size={16} />
            Download ZIP
          </button>
        ) : (
          <span
            className="cursor-not-allowed"
            style={{ color: "var(--main-text-muted)" }}
            title="License expired"
          >
            Download disabled
          </span>
        ),
    },
  ];

  return (
    <div className="p-4 sm:p-6 min-h-screen w-full" style={{ background: "var(--main-bg)", color: "var(--main-text)" }}>

      <PageHeader
        icon={<FileLock size={28} strokeWidth={2.2} />}
        title="Licenses"
        subtitle="Generate and manage licenses"
        meta={isPageLoading ? null : `${total} ${total === 1 ? "license" : "licenses"}`}
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 font-medium px-4 h-10 rounded-lg transition hover:opacity-90"
            style={{ background: "var(--brand)", color: "#fff" }}
          >
            <Plus size={18} />
            Generate License
          </button>
        }
      />

      {/* FILTERS */}
      <div className="p-4 sm:p-5 rounded-lg mb-6 border" style={{ background: "var(--card-bg)", borderColor: "var(--card-border)" }}>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">

          <div className="flex-1 min-w-0">
            <label htmlFor="license-search" className="text-sm mb-2 block" style={{ color: "var(--main-text)" }}>
              Search
            </label>
            <div className="relative">
              <Search size={18} className="absolute left-3 top-2.5 pointer-events-none" style={{ color: "var(--main-text-muted)" }} />
              <input
                id="license-search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-9 h-10 border rounded-lg"
                style={{
                  background: "var(--input-background)",
                  color: "var(--main-text)",
                  borderColor: "var(--card-border)",
                }}
                placeholder="Search by License ID"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setPage(1); }}
                  aria-label="Clear search"
                  className="absolute right-2 top-2.5 p-0.5 rounded hover:opacity-70"
                  style={{ color: "var(--main-text-muted)" }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="license-status" className="text-sm mb-2 block" style={{ color: "var(--main-text)" }}>
              Status
            </label>
            <select
              id="license-status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full sm:w-[140px] h-10 border rounded-lg px-3"
              style={{
                background: "var(--input-background)",
                color: "var(--main-text)",
                borderColor: "var(--card-border)",
              }}
            >
              <option value="all">All</option>
              <option value="ACTIVE">Active</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>

          <button
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "var(--brand-light)", color: "var(--brand)" }}
          >
            <ListRestart size={16} />
            Reset
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredLicenses}
        loading={isPageLoading || isTableLoading}
        getRowKey={(l) => l._id}
        emptyState={
          hasActiveFilters ? (
            <EmptyState
              icon={<Search size={26} />}
              title="No licenses match these filters"
              description="Try a different License ID, or clear the filters to see every license."
              action={
                <button
                  onClick={clearFilters}
                  className="h-10 px-4 rounded-lg font-medium border transition hover:opacity-80"
                  style={{ color: "var(--brand)", borderColor: "var(--brand)" }}
                >
                  Clear filters
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={<FileLock size={26} />}
              title="No licenses yet"
              description="Generate the first license to see it listed here."
              action={
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 h-10 px-4 rounded-lg font-medium"
                  style={{ background: "var(--brand)", color: "#fff" }}
                >
                  <Plus size={18} /> Generate License
                </button>
              }
            />
          )
        }
      />

      <Pagination page={page} totalPages={totalPages} totalRecords={total} onPageChange={setPage} />

      {/* MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
          <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-xl p-6">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Generate License
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <X />
              </button>
            </div>

            <LicenseForm
              onSubmit={handleCreate}
              isLoading={isSubmitting}
              clients={clients}
              partners={partners}
              rans={rans}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default License;
